import type { Reference } from "../db";

// Function to extract referenced IDs from markdown content with display numbers and order
export const extractReferencedIdsWithNumbers = (
    content: string,
): Array<{ id: string; displayNumber: number; order: number }> => {
    const referencedIds: Array<{ id: string; displayNumber: number; order: number }> = [];
    const regex = /\[([a-zA-Z0-9, ]+)\](?!\()/g;
    const matches = Array.from(content.matchAll(regex));
    const seenIds = new Map<string, number>();
    let nextNumber = 1;
    let globalOrder = 0;

    for (const match of matches) {
        const refIds = match[1].split(",").map((id: string) => id.trim());
        const validIds = refIds.filter((refId: string) => /^[a-zA-Z0-9]{8}$/.test(refId));

        for (const refId of validIds) {
            if (!seenIds.has(refId)) {
                const displayNumber = nextNumber++;
                seenIds.set(refId, displayNumber);
                referencedIds.push({
                    id: refId,
                    displayNumber: displayNumber,
                    order: globalOrder++,
                });
            }
        }
    }

    return referencedIds;
};

// Function to process markdown content and replace bracketed references (e.g. "[7ffd7af9, efddec95]")
// with a custom self-closing tag <ref index="1" />.
export const processMarkdownContent = (content: string, references?: Reference[]) => {
    const refIdToNumberMap = new Map<string, number>();
    let nextNumber = 1;
    // Build a Set of valid reference IDs for O(1) lookup (once, outside the replace callback)
    const referenceIdSet = references
        ? new Set(references.map((ref) => ref.reference_id))
        : new Set<string>();

    return content.replace(/\[([a-zA-Z0-9, ]+)\](?!\()/g, (match, refContent) => {
        // Split by comma and trim whitespace
        const refIds = refContent.split(",").map((id: string) => id.trim());

        // Validate and process each reference ID in a single pass
        const validRefs: string[] = [];
        for (const refId of refIds) {
            if (!/^[a-zA-Z0-9]{8}$/.test(refId)) continue;
            if (!referenceIdSet.has(refId)) continue;
            // Get or assign a sequential number for this reference ID
            if (!refIdToNumberMap.has(refId)) {
                refIdToNumberMap.set(refId, nextNumber++);
            }
            const displayNumber = refIdToNumberMap.get(refId);
            validRefs.push(
                `<inline-ref index="${refId}" display="${displayNumber}"/></inline-ref>`,
            );
        }

        if (validRefs.length > 0) {
            return validRefs.join("");
        }

        // Check if the content contains potential internal IDs (8 alphanumeric chars)
        // If so, don't show them to avoid exposing internal reference IDs
        const hasPotentialIds = refIds.some((refId: string) => /^[a-zA-Z0-9]{8}$/.test(refId));
        if (hasPotentialIds) {
            // Return empty string to hide internal IDs that don't have valid references
            return "";
        }

        return match;
    });
};
